import React, { useEffect, useState } from 'react';
import { BackHandler } from 'react-native';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet } from '../components/rn';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import PageHeader from '../components/PageHeader';
import { screenHeaderContentInset } from '../components/screenHeaderLayout';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { profilePalette } from '../theme/profileSection';
import { canPreviewConsent, IDENTITY_DRAFT_VERSION, identityDraftCopy, identitySamples } from '../identity/verificationPreview';

/** Review-only: no token, API, URL launch, capture, persistence, or real account state. */
export default function IdentityVerificationPreviewScreen({ onBack }: { onBack: () => void }) {
  const { lang } = useTranslation();
  const c = identityDraftCopy[lang === 'tr' ? 'tr' : 'en'];
  const colors = useTheme();
  const palette = profilePalette(colors);
  const insets = useSafeAreaInsets();
  const [page, setPage] = useState<'intro' | 'consent' | 'notice' | 'end' | 'samples' | 'rights'>('intro');
  const [biometric, setBiometric] = useState(false);
  const [notice, setNotice] = useState(false);
  const [noticeFrom, setNoticeFrom] = useState<'intro' | 'consent'>('intro');
  const reset = () => { setBiometric(false); setNotice(false); setPage('intro'); };
  const back = () => {
    if (page === 'intro') onBack();
    else if (page === 'notice') setPage(noticeFrom);
    else reset();
  };
  useEffect(() => {
    const listener = BackHandler.addEventListener('hardwareBackPress', () => { back(); return true; });
    return () => listener.remove();
  });
  // A locale change must not carry agreement to different wording.
  useEffect(() => { setBiometric(false); setNotice(false); }, [lang]);
  const button = (id: string, label: string, onPress: () => void, disabled = false) => (
    <TouchableOpacity key={id} testID={id} accessibilityRole="button" accessibilityState={{ disabled }}
      disabled={disabled} onPress={onPress} style={[s.button, { borderColor: colors.gray200 }, disabled && s.disabled]}>
      <Text style={[s.text, s.buttonText, { color: colors.black }]}>{label}</Text>
    </TouchableOpacity>
  );
  const paragraph = (text: string, id?: string) => <Text testID={id} style={[s.text, { color: colors.black }]}>{text}</Text>;
  const checkbox = (id: string, label: string, checked: boolean, toggle: () => void) => (
    <TouchableOpacity testID={id} accessibilityRole="checkbox" accessibilityLabel={label}
      accessibilityState={{ checked }} onPress={toggle} style={s.check}>
      <Ionicons name={checked ? 'checkbox' : 'square-outline'} size={26} color={palette.link} accessible={false} />
      <Text style={[s.text, s.checkText, { color: colors.black }]}>{label}</Text>
    </TouchableOpacity>
  );
  return <View style={[s.screen, { backgroundColor: colors.white }]} testID="identity-preview-screen">
    <PageHeader title={c.title} onBack={back} floating backTestID="identity-preview-back" />
    <ScrollView key={page} testID="identity-preview-scroll" contentContainerStyle={[s.content, {
      paddingTop: screenHeaderContentInset(insets.top), paddingBottom: insets.bottom + 32,
    }]}>
      <Text style={[s.banner, { color: palette.warmAccent }]}>{c.draft}</Text>
      {lang !== 'tr' && lang !== 'en' && paragraph('English legal-review draft. Translations require approval before activation.', 'identity-language-warning')}
      {page === 'intro' && <>
        {paragraph(c.intro)}
        {button('identity-preview-open', c.open, () => { setBiometric(false); setNotice(false); setPage('consent'); })}
        {button('identity-preview-notice', c.notice, () => { setNoticeFrom('intro'); setPage('notice'); })}
        {button('identity-preview-samples', c.samples, () => setPage('samples'))}
        {button('identity-preview-rights', c.rights, () => setPage('rights'))}
      </>}
      {page === 'consent' && <>
        {paragraph(c.warning)}
        {button('identity-preview-notice', c.notice, () => { setNoticeFrom('consent'); setPage('notice'); })}
        {checkbox('identity-biometric-consent', c.biometric, biometric, () => setBiometric(!biometric))}
        {checkbox('identity-notice-ack', c.acknowledge, notice, () => setNotice(!notice))}
        {button('identity-preview-continue', c.continue, () => {
          if (canPreviewConsent(biometric, notice)) { setBiometric(false); setNotice(false); setPage('end'); }
        }, !canPreviewConsent(biometric, notice))}
        {button('identity-preview-cancel', c.cancel, reset)}
      </>}
      {page === 'notice' && <>{paragraph(c.noticeText)}{paragraph(IDENTITY_DRAFT_VERSION)}</>}
      {page === 'end' && <>
        <Text accessibilityRole="header" style={[s.heading, { color: colors.black }]}>{c.complete}</Text>
        {paragraph(c.stopped, 'identity-preview-stopped')}
        {button('identity-preview-reset', c.reset, reset)}
      </>}
      {page === 'samples' && identitySamples.map(status => <View key={status} style={[s.sample, { borderColor: colors.gray200 }]}>
        <Text style={[s.banner, { color: palette.warmAccent }]}>{c.sample}</Text>
        {paragraph(c.statuses[status], `identity-sample-${status}`)}
      </View>)}
      {page === 'rights' && <>{paragraph(c.rightsText)}{button('identity-preview-reset', c.reset, reset)}</>}
    </ScrollView>
  </View>;
}
const s = StyleSheet.create({
  screen: { flex: 1 }, content: { paddingHorizontal: 20, gap: 16 },
  text: { fontSize: 16, lineHeight: 25, flexShrink: 1 }, banner: { fontSize: 13, lineHeight: 20, fontWeight: '600' },
  heading: { fontSize: 22, fontWeight: '600' }, buttonText: { fontWeight: '600' },
  button: { minHeight: 48, paddingVertical: 12, paddingHorizontal: 16, borderWidth: 1, borderRadius: 16, justifyContent: 'center' },
  disabled: { opacity: 0.45 }, check: { minHeight: 48, paddingVertical: 12, flexDirection: 'row', gap: 12, alignItems: 'flex-start' },
  checkText: { flex: 1, minWidth: 0 }, sample: { paddingVertical: 16, gap: 8, borderBottomWidth: 1 },
});
